import React, { useRef, useCallback, useState, useEffect } from "react";
import useTranslator from "../../../hooks/useTranslator";

interface CaptureScreenProps {
  side: "front" | "back";
  onCapture: (blob: File | Blob) => void;
  onCancel: () => void;
}

const CaptureScreen: React.FC<CaptureScreenProps> = ({
  side,
  onCapture,
  onCancel,
}) => {
  const { _t } = useTranslator();
  const tRef = useRef(_t);
  tRef.current = _t;

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraReady(true);
      } catch (err: any) {
        if (!cancelled) {
          const message =
            err.name === "NotAllowedError"
              ? tRef.current("mobileCaptureCameraPermission")
              : tRef.current("mobileCaptureCameraError");
          setCameraError(message);
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handleCapture = useCallback(() => {
    if (isCapturing) return;

    const video = videoRef.current;
    if (!video) return;
    setIsCapturing(true);

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `${side}_${Date.now()}.jpeg`, {
            type: "image/jpeg",
          });
          onCapture(file);
        }
      },
      "image/jpeg",
      0.92
    );

    setTimeout(() => setIsCapturing(false), 300);
  }, [isCapturing, side, onCapture]);

  if (cameraError) {
    return (
      <div className="mobile-capture-camera-error">
        <div className="mobile-capture-error-icon">!</div>
        <h3>{_t("mobileCaptureCameraIssue")}</h3>
        <p>{cameraError}</p>
        <button
          className="mobile-capture-btn mobile-capture-btn-secondary"
          onClick={onCancel}
        >
          {_t("mobileCaptureGoBack")}
        </button>
      </div>
    );
  }

  return (
    <div className="mobile-capture-camera-screen">
      <div className="mobile-capture-camera-header">
        <span className="mobile-capture-camera-step">
          {_t("Photo")} {side === "front" ? "1" : "2"}
        </span>
        <strong>
          {side === "front" ? _t("Front of document") : _t("Back of document")}
        </strong>
      </div>
      <div className="mobile-capture-camera-container">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="mobile-capture-camera-video"
          onLoadedMetadata={() => {
            videoRef.current?.play();
          }}
        />
        <div className="mobile-capture-overlay-frame">
          <div className="mobile-capture-overlay-corners" />
        </div>

        <div className="mobile-capture-camera-hint">
          <p>
            {side === "front"
              ? _t("mobileCaptureHintFront")
              : _t("mobileCaptureHintBack")}
          </p>
        </div>
      </div>

      <div className="mobile-capture-camera-controls">
        <button
          type="button"
          className="mobile-capture-capture-btn"
          onClick={handleCapture}
          disabled={!cameraReady || isCapturing}
          aria-label={_t("mobileCaptureShutter")}
        >
          <span className="mobile-capture-shutter-ring" />
        </button>
      </div>
    </div>
  );
};

export default CaptureScreen;
